import { ApiResult, PaginatedResponse, request, safeRequest } from '@synergy/api';
import { EmployeePermissions, Position } from '@synergy/models';
import { noop } from '@synergy/utils';

import { Contact, ContactMeta } from '../../common/models/contact';
import { ContactRequest } from './models/contact-request';
import { LoginUser } from '../../features/contacts-logins/models/login-user';

export class ContactsLoginsApi {
    constructor(protected baseUrl: string) {}

    public cget = (
        params?: URLSearchParams
    ): Promise<ApiResult<PaginatedResponse<Contact | LoginUser, ContactMeta>>> => {
        return safeRequest(
            () => request.get(`${this.baseUrl}/contacts`, { params }),
            response => response.data
        );
    };

    public deleteLoginUser = (user: Contact): Promise<ApiResult<void>> => {
        return safeRequest(
            () => request.patch(`${this.baseUrl}/contacts/${user.id}`, { ...user, enableLogin: false }),
            noop
        );
    };

    public getPositions = (): Promise<ApiResult<Position[]>> => {
        return safeRequest(
            () => request.get(`${this.baseUrl}/positions`),
            response => response.data
        );
    };

    public getOrderCeilings = (): Promise<ApiResult<number[]>> => {
        return safeRequest(
            () => request.get(`${this.baseUrl}/contact-order-ceilings`),
            response => response.data
        );
    };

    public updateContact = (contact: ContactRequest): Promise<ApiResult<Contact | LoginUser>> => {
        return safeRequest(
            () => request.patch(`${this.baseUrl}/contacts/${contact.id}`, contact),
            response => response.data
        );
    };

    public createUser = (contact: ContactRequest): Promise<ApiResult<Contact | LoginUser>> => {
        return safeRequest(
            () => request.post(`${this.baseUrl}/contacts`, contact),
            response => response.data
        );
    };

    public removeEmployee = (
        contactId: number,
        assignTopicsToContactId?: number,
        assignMainContactId?: number,
        assignBonusContactId?: number
    ): Promise<ApiResult<void>> =>
        safeRequest(
            () =>
                request.post(`${this.baseUrl}/contacts/${contactId}/remove-employee`, {
                    assignTopicsToContactId,
                    assignMainContactId,
                    assignBonusContactId,
                }),
            noop
        );

    public changeEmployeePermission(
        user: Contact | LoginUser,
        permissions: EmployeePermissions[]
    ): Promise<ApiResult<void>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/contacts/${user.id}/update-employee-permissions`, { permissions }),
            noop
        );
    }

    public updateMainContact = (mainContactId: number): Promise<ApiResult<void>> => {
        return safeRequest(
            () =>
                request.post(`${this.baseUrl}/contacts/update-main-contact`, {
                    mainContactId,
                }),
            noop
        );
    };

    public updateBonusContact = (
        id: number,
        isBonusContact: boolean,
        assignBonusContactId?: number
    ): Promise<ApiResult<void>> => {
        return safeRequest(
            () =>
                request.post(`${this.baseUrl}/contacts/update-bonus-contact`, {
                    id,
                    isBonusContact,
                    assignBonusContactId,
                }),
            noop
        );
    };
}
